import React, { useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Chip,
  Menu,
  MenuItem,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Add as AddIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Description as DescriptionIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  TableChart as ExcelIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const DocumentsList = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterAnchor, setFilterAnchor] = useState(null);

  const documents = [
    {
      id: 1,
      name: 'Invoice_INV-001.pdf',
      type: 'Invoice',
      size: '245 KB',
      uploadedDate: '2024-06-15',
      uploadedBy: 'Admin',
      category: 'Sales',
    },
    {
      id: 2,
      name: 'Quotation_QUO-001.pdf',
      type: 'Quotation',
      size: '180 KB',
      uploadedDate: '2024-06-14',
      uploadedBy: 'Admin',
      category: 'Sales',
    },
    {
      id: 3,
      name: 'Delivery_Challan_CHL-001.pdf',
      type: 'Delivery Challan',
      size: '320 KB',
      uploadedDate: '2024-06-13',
      uploadedBy: 'Admin',
      category: 'Sales',
    },
    {
      id: 4,
      name: 'Purchase_Order_PO-001.pdf',
      type: 'Purchase Order',
      size: '210 KB',
      uploadedDate: '2024-06-12',
      uploadedBy: 'Admin',
      category: 'Purchases',
    },
    {
      id: 5,
      name: 'Bill_BILL-001.pdf',
      type: 'Bill',
      size: '195 KB',
      uploadedDate: '2024-06-10',
      uploadedBy: 'Admin',
      category: 'Purchases',
    },
    {
      id: 6,
      name: 'Stock_Report_June_2024.xlsx',
      type: 'Report',
      size: '450 KB',
      uploadedDate: '2024-06-08',
      uploadedBy: 'Admin',
      category: 'Inventory',
    },
  ];

  const getFileIcon = (fileName) => {
    if (fileName.endsWith('.pdf')) return <PdfIcon sx={{ color: '#EF4444' }} />;
    if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) return <ExcelIcon sx={{ color: '#10B981' }} />;
    if (fileName.endsWith('.jpg') || fileName.endsWith('.png')) return <ImageIcon sx={{ color: '#2563EB' }} />;
    return <DescriptionIcon sx={{ color: '#64748B' }} />;
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'Sales': return '#10B981';
      case 'Purchases': return '#EF4444';
      case 'Inventory': return '#F59E0B';
      case 'Banking': return '#2563EB';
      default: return '#64748B';
    }
  };

  const handleFilterClick = (event) => {
    setFilterAnchor(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchor(null);
  };

  const filteredDocuments = documents.filter((doc) =>
    doc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    doc.type.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontSize: '2rem', fontWeight: 600, color: '#1E293B', mb: 1 }}>
          Documents
        </Typography>
        <Typography variant="body1" sx={{ color: '#64748B', fontSize: '0.875rem' }}>
          Manage and organize your business documents
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <motion.div whileHover={{ scale: 1.02 }} transition={{ duration: 0.2 }}>
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
                border: '1px solid #E2E8F0',
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography variant="body2" sx={{ color: '#64748B', fontSize: '0.875rem', mb: 1 }}>
                  Total Documents
                </Typography>
                <Typography variant="h4" sx={{ fontSize: '2rem', fontWeight: 600, color: '#1E293B' }}>
                  {documents.length}
                </Typography>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <motion.div whileHover={{ scale: 1.02 }} transition={{ duration: 0.2 }}>
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
                border: '1px solid #E2E8F0',
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography variant="body2" sx={{ color: '#64748B', fontSize: '0.875rem', mb: 1 }}>
                  Total Size
                </Typography>
                <Typography variant="h4" sx={{ fontSize: '2rem', fontWeight: 600, color: '#2563EB' }}>
                  1.6 MB
                </Typography>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <motion.div whileHover={{ scale: 1.02 }} transition={{ duration: 0.2 }}>
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
                border: '1px solid #E2E8F0',
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography variant="body2" sx={{ color: '#64748B', fontSize: '0.875rem', mb: 1 }}>
                  This Month
                </Typography>
                <Typography variant="h4" sx={{ fontSize: '2rem', fontWeight: 600, color: '#10B981' }}>
                  {documents.length}
                </Typography>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <motion.div whileHover={{ scale: 1.02 }} transition={{ duration: 0.2 }}>
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
                border: '1px solid #E2E8F0',
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography variant="body2" sx={{ color: '#64748B', fontSize: '0.875rem', mb: 1 }}>
                  Categories
                </Typography>
                <Typography variant="h4" sx={{ fontSize: '2rem', fontWeight: 600, color: '#F59E0B' }}>
                  4
                </Typography>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
      </Grid>

      {/* Action Bar */}
      <Card
        sx={{
          borderRadius: 3,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
          border: '1px solid #E2E8F0',
          mb: 3,
        }}
      >
        <CardContent sx={{ p: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search documents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#64748B' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  startIcon={<FilterListIcon />}
                  onClick={handleFilterClick}
                  sx={{ borderRadius: 2 }}
                >
                  Filter
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  sx={{ borderRadius: 2 }}
                >
                  Upload
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  sx={{ borderRadius: 2 }}
                >
                  Download All
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  sx={{
                    backgroundColor: '#2563EB',
                    borderRadius: 2,
                    '&:hover': {
                      backgroundColor: '#1E40AF',
                    },
                  }}
                >
                  New Folder
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Filter Menu */}
      <Menu
        anchorEl={filterAnchor}
        open={Boolean(filterAnchor)}
        onClose={handleFilterClose}
        PaperProps={{
          sx: {
            minWidth: 200,
            borderRadius: 2,
          },
        }}
      >
        <MenuItem onClick={handleFilterClose}>All Documents</MenuItem>
        <MenuItem onClick={handleFilterClose}>Sales</MenuItem>
        <MenuItem onClick={handleFilterClose}>Purchases</MenuItem>
        <MenuItem onClick={handleFilterClose}>Inventory</MenuItem>
        <MenuItem onClick={handleFilterClose}>Banking</MenuItem>
        <MenuItem onClick={handleFilterClose}>Reports</MenuItem>
      </Menu>

      {/* Documents Grid */}
      <Grid container spacing={3}>
        {filteredDocuments.map((doc) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={doc.id}>
            <motion.div whileHover={{ scale: 1.02 }} transition={{ duration: 0.2 }}>
              <Card
                sx={{
                  borderRadius: 3,
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
                  border: '1px solid #E2E8F0',
                  cursor: 'pointer',
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Box sx={{ fontSize: 40 }}>{getFileIcon(doc.name)}</Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: '#1E293B',
                          fontSize: '0.875rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {doc.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#64748B', fontSize: '0.75rem' }}>
                        {doc.size}
                      </Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Chip
                      label={doc.category}
                      size="small"
                      sx={{
                        backgroundColor: `${getCategoryColor(doc.category)}15`,
                        color: getCategoryColor(doc.category),
                        fontWeight: 500,
                      }}
                    />
                    <Typography variant="caption" sx={{ color: '#64748B', fontSize: '0.75rem' }}>
                      {doc.uploadedDate}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton size="small" sx={{ color: '#2563EB' }}>
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" sx={{ color: '#10B981' }}>
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" sx={{ color: '#EF4444' }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default DocumentsList;
